import { isRecord } from '../helper';
import type { HomepageHwsRlmRetargetingCandidate } from './retargeting.types';

/**
 * RLM search criteria captured from the guest's last availability search.
 * Values keep the Shop funnel's own formats (dates MM/DD/YYYY, childrenAges
 * comma-joined) so they can be appended to availabilitySearch.mi verbatim.
 * `marshaCode` (lowercased) links the criteria to a retargeting candidate.
 */
export interface RlmSearchCriteria {
  marshaCode: string;
  /**
   * True when the session's last search was flexible-dates. The funnel still
   * records default concrete dates for flexible searches (today/tomorrow), so
   * dates must not be treated as guest intent when this is set.
   */
  flexibleSearch?: boolean;
  fromDate?: string;
  toDate?: string;
  numberOfRooms?: string;
  numberOfAdults?: string;
  childrenCount?: string;
  childrenAges?: string;
  clusterCode?: string;
  placeId?: string;
  searchRadius?: string;
}

export type RlmStayDatesStatus = 'valid' | 'invalid' | 'absent';

export type HwsRlmPathingResolution = { action: 'accept'; targetUrl: string } | { action: 'skip' };

const AVAILABILITY_SEARCH_BASE = '/reservation/availabilitySearch.mi?isSearch=false&propertyCode=';
const FUNNEL_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const CHILDREN_AGES_PATTERN = /^\d{1,2}(,\d{1,2})*$/;

export function buildFlexibleRlmTargetUrl(marshaCode: string): string {
  return `${AVAILABILITY_SEARCH_BASE}${marshaCode}&flexibleDateSearch=true`;
}

function getString(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

function getQueryParams(source: Record<string, unknown>): URLSearchParams | null {
  const raw = getString(source, 'page_url_query_string');
  if (!raw) return null;
  // page_url_query_string can arrive &amp;-encoded (same normalization as
  // targetBrandBannerPayload's parsing of this field).
  const normalized = raw.replace(/&amp;/g, '&');
  return new URLSearchParams(normalized.startsWith('?') ? normalized.slice(1) : normalized);
}

function asCount(value: string, minimum: number): string {
  return /^\d+$/.test(value) && Number(value) >= minimum ? value : '';
}

function asChildrenAges(value: string): string {
  return CHILDREN_AGES_PATTERN.test(value) ? value : '';
}

/**
 * Extract RLM search criteria from an Adobe data layer state record. Query
 * params (`page_url_query_string`) carry the funnel's own names and win over
 * the derived data layer fields. Returns null without a property to link to.
 */
export function readRlmSearchCriteria(state: unknown): RlmSearchCriteria | null {
  if (!isRecord(state)) return null;
  const source = state as Record<string, unknown>;
  const marshaCode = getString(source, 'prop_marsha_code').toLowerCase();
  if (!marshaCode) return null;

  const query = getQueryParams(source);
  const pick = (queryKey: string, dataLayerKey?: string): string => {
    const fromQuery = query?.get(queryKey)?.trim();
    if (fromQuery) return fromQuery;
    return dataLayerKey ? getString(source, dataLayerKey) : '';
  };

  const criteria: RlmSearchCriteria = { marshaCode };
  const flexibleFlag =
    pick('flexibleDateSearch', 'search_is_dates_flexible') || getString(source, 'search_dates_flexible');
  if (flexibleFlag === 'true') criteria.flexibleSearch = true;
  const assign = (key: keyof Omit<RlmSearchCriteria, 'marshaCode' | 'flexibleSearch'>, value: string): void => {
    if (value) criteria[key] = value;
  };

  assign('fromDate', pick('fromDate', 'search_date_check_in'));
  assign('toDate', pick('toDate', 'search_date_check_out'));
  assign('numberOfRooms', asCount(pick('numberOfRooms'), 1));
  assign('numberOfAdults', asCount(pick('numberOfAdults', 'res_num_adults'), 1));
  assign('childrenCount', asCount(pick('childrenCount', 'res_num_children'), 0));
  assign('childrenAges', asChildrenAges(pick('childrenAges')));
  assign('clusterCode', pick('clusterCode', 'search_cluster_code'));
  assign('placeId', pick('destinationAddress.placeId'));
  assign('searchRadius', asCount(pick('searchRadius'), 1));
  return criteria;
}

function parseFunnelDateMs(value: string | undefined): number | null {
  if (!value) return null;
  const match = FUNNEL_DATE_PATTERN.exec(value.trim());
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  const roundTrips = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return roundTrips ? date.getTime() : null;
}

/**
 * Only past/malformed dates are detectable client-side (sold-out is not);
 * a stay starting today is still valid.
 */
export function evaluateRlmStayDates(criteria: RlmSearchCriteria, nowMs: number): RlmStayDatesStatus {
  if (!criteria.fromDate && !criteria.toDate) return 'absent';
  const fromMs = parseFunnelDateMs(criteria.fromDate);
  const toMs = parseFunnelDateMs(criteria.toDate);
  if (fromMs === null || toMs === null) return 'invalid';
  const todayStart = new Date(nowMs);
  todayStart.setHours(0, 0, 0, 0);
  if (fromMs < todayStart.getTime() || toMs <= fromMs) return 'invalid';
  return 'valid';
}

export function buildEnrichedRlmTargetUrl(marshaCode: string, criteria: RlmSearchCriteria): string {
  const params: Array<[string, string | undefined]> = [
    ['fromDate', criteria.fromDate],
    ['toDate', criteria.toDate],
    ['flexibleDateSearch', 'false'],
    ['numberOfRooms', criteria.numberOfRooms],
    ['numberOfAdults', criteria.numberOfAdults],
    ['childrenCount', criteria.childrenCount],
    ['childrenAges', criteria.childrenAges],
    ['clusterCode', criteria.clusterCode],
    ['destinationAddress.placeId', criteria.placeId],
    ['searchRadius', criteria.searchRadius],
  ];
  const suffix = params
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
  return `${AVAILABILITY_SEARCH_BASE}${marshaCode}&${suffix}`;
}

/**
 * PRD "skip-to-next" rule for one HWS/RLM candidate: criteria linked to the
 * candidate with valid dates -> enriched RLM URL; linked with invalid dates ->
 * skip the candidate entirely; not linked (or no dates) -> flexible-date
 * Availability Calendar.
 */
export function resolveHwsRlmCandidatePathing(
  candidate: HomepageHwsRlmRetargetingCandidate,
  criteria: RlmSearchCriteria | null | undefined,
  nowMs: number
): HwsRlmPathingResolution {
  if (!criteria || criteria.marshaCode !== candidate.marshaCode) {
    return { action: 'accept', targetUrl: buildFlexibleRlmTargetUrl(candidate.marshaCode) };
  }
  // A flexible-dates search carries funnel-default dates (today/tomorrow), not
  // guest-chosen ones — never pin them; keep the flexible-date CTA instead.
  if (criteria.flexibleSearch) {
    return { action: 'accept', targetUrl: buildFlexibleRlmTargetUrl(candidate.marshaCode) };
  }
  const status = evaluateRlmStayDates(criteria, nowMs);
  if (status === 'invalid') return { action: 'skip' };
  if (status === 'valid') {
    return { action: 'accept', targetUrl: buildEnrichedRlmTargetUrl(candidate.marshaCode, criteria) };
  }
  return { action: 'accept', targetUrl: buildFlexibleRlmTargetUrl(candidate.marshaCode) };
}

/**
 * Client-side criteria read. Prefers ACDL (window.adobeDataLayer.getState())
 * which is what the Homepage runs on, and falls back to the session datalayer
 * for the non-ACDL path. Null on the server or when nothing is readable.
 */
export function readClientRlmSearchCriteria(sessionDatalayer?: Record<string, unknown>): RlmSearchCriteria | null {
  if (typeof window === 'undefined') return null;
  const acdlState = window.adobeDataLayer?.getState?.();
  // Live ACDL getState() returns a wrapper { data, skipDatalayerUpdate } with the
  // field map under `data` (verified on UAT3); unwrap it, but accept a flat record
  // too so non-ACDL and stubbed states keep working.
  const unwrapped =
    isRecord(acdlState) && isRecord((acdlState as Record<string, unknown>)['data'])
      ? ((acdlState as Record<string, unknown>)['data'] as Record<string, unknown>)
      : acdlState;
  const fromAcdl = readRlmSearchCriteria(unwrapped);
  return fromAcdl ?? readRlmSearchCriteria(sessionDatalayer);
}
